/**
 * Reddit Ads Pixel — thin typed wrapper around the global `rdt` injected
 * by the snippet in public/index.html.
 *
 * All functions are no-ops when the pixel hasn't loaded (ad blockers,
 * dev with no network, etc.) — never throws, never blocks user flow.
 *
 * Pixel ID: a2_j234lap2s75v
 *
 * Standard event taxonomy (matches Reddit's catalogue so ad-set
 * optimisation works without custom-event mapping):
 *   PageVisit  — fired automatically by the snippet + by trackPageVisit()
 *                on SPA route changes
 *   SignUp     — user submitted register form (account created)
 *   Lead       — user finished the 4-step onboarding wizard
 *   Purchase   — user upgraded to Pro (with value + currency)
 */

declare global {
  interface Window {
    rdt?: (
      command: 'track' | 'init',
      eventName: string,
      params?: Record<string, unknown>,
    ) => void;
  }
}

const isAvailable = (): boolean => typeof window !== 'undefined' && typeof window.rdt === 'function';

/** Fire PageVisit on SPA route changes. */
export function trackPageVisit(): void {
  if (!isAvailable()) return;
  try { window.rdt!('track', 'PageVisit'); } catch { /* ignore */ }
}

/** Fire when a user completes register (account exists in DB). */
export function trackSignUp(extras?: { method?: string }): void {
  if (!isAvailable()) return;
  try {
    window.rdt!('track', 'SignUp', extras as Record<string, unknown>);
  } catch { /* ignore */ }
}

/** Fire when a user finishes the onboarding wizard (qualified lead). */
export function trackLead(extras?: { method?: string }): void {
  if (!isAvailable()) return;
  try {
    window.rdt!('track', 'Lead', extras as Record<string, unknown>);
  } catch { /* ignore */ }
}

/**
 * Fire when a user upgrades to Pro. value+currency let Reddit's algorithm
 * optimise toward higher-ARPU prospects.
 */
export function trackPurchase(params: { value: number; currency?: string; plan?: string }): void {
  if (!isAvailable()) return;
  try {
    window.rdt!('track', 'Purchase', {
      value: params.value,
      currency: params.currency || 'USD',
      itemCount: 1,
      ...(params.plan ? { plan: params.plan } : {}),
    });
  } catch { /* ignore */ }
}
